/**
 * 大气物理参数模型。
 * 统一使用 km 作为长度单位；散射系数单位为 1/km。
 * 参考：Nishita 1993 / 常见 ray-marching 教学实现（Marten 的 atmospheric scattering shader）。
 */

import type { Vec3 } from './raySphere'

/** 可调参数（对应需求优先级 2 的参数面板） */
export interface AtmosphereParams {
  /** 太阳仰角（度），-10 ~ 90 */
  sunElevationDeg: number
  /** 太阳方位角（度），0 ~ 360 */
  sunAzimuthDeg: number
  /** 浊度（Turbidity），1 ~ 10，影响 Mie 散射强度 */
  turbidity: number
  /** 气溶胶密度（Aerosol Density），0 ~ 1 */
  aerosolDensity: number
  /** 臭氧吸收强度，0 ~ 1（预留，Phase B 接入着色器） */
  ozoneAbsorption: number
  /** 相机海拔（km），0 ~ 100+ */
  altitudeKm: number
}

/** 由可调参数导出的物理常数/系数，用于着色器与 LUT */
export interface AtmosphereCoeffs {
  /** Rayleigh 散射系数（1/km），RGB 三通道 */
  betaR: Vec3
  /** Mie 散射系数（1/km） */
  betaM: Vec3
  /** Mie 消光系数（1/km），含吸收 */
  betaMExt: Vec3
  /** Rayleigh 特征高度（km） */
  Hr: number
  /** Mie 特征高度（km） */
  Hm: number
  /** 地球半径（km） */
  R: number
  /** 大气层顶半径（km） */
  Rt: number
  /** Henyey-Greenstein 各向异性参数 */
  g: number
  /** 指向太阳的单位方向 */
  sunDir: Vec3
  /** Mie 系数缩放（由浊度/气溶胶密度导出） */
  mieScale: number
  /** 地表环境光（来自天空） */
  skyAmbient: Vec3
}

export const PLANET_RADIUS_KM = 6371
export const ATMOSPHERE_RADIUS_KM = 6471
export const RAYLEIGH_SCALE_HEIGHT_KM = 8.0
export const MIE_SCALE_HEIGHT_KM = 1.2
export const MIE_G = 0.76
/** 太阳角半径（弧度），约 0.27° */
export const SUN_ANGULAR_RADIUS = 0.004675
/** 太阳辐照度标量（用于 HDR 亮度量级） */
export const SUN_IRRADIANCE = 20.0

// Rayleigh 散射系数基准（1/km），对应波长约 [650, 532, 440] nm
const BETA_R_BASE: Vec3 = [5.5e-3, 13.0e-3, 22.4e-3]
// Mie 散射系数基准（1/km）；清澈天通过 mieScale 缩放，使其明显弱于 Rayleigh 蓝通道
const BETA_M_BASE = 12e-3

/**
 * 由仰角/方位角计算指向太阳的单位向量。
 * 采用“极点本地天球”约定：+Z 为天顶（与极点相机一致），
 * 方位角 0° 指向 +X（初始视角方向），仰角 90° 即天顶。
 */
export function sunDirection(elevDeg: number, azimDeg: number): Vec3 {
  const el = (elevDeg * Math.PI) / 180
  const az = (azimDeg * Math.PI) / 180
  const ce = Math.cos(el)
  return [ce * Math.cos(az), ce * Math.sin(az), Math.sin(el)]
}

/** 由可调参数导出最终物理系数 */
export function computeCoeffs(p: AtmosphereParams): AtmosphereCoeffs {
  // 清澈天 (turbidity≈1, aerosol≈0) → ~0.3；重度雾霾 → ~3+
  const mieScale =
    0.3 + (p.turbidity - 1) * 0.22 + p.aerosolDensity * 1.0
  const betaM = BETA_M_BASE * mieScale
  return {
    betaR: [...BETA_R_BASE] as Vec3,
    betaM: [betaM, betaM, betaM],
    betaMExt: [betaM * 1.1, betaM * 1.1, betaM * 1.1],
    Hr: RAYLEIGH_SCALE_HEIGHT_KM,
    Hm: MIE_SCALE_HEIGHT_KM,
    R: PLANET_RADIUS_KM,
    Rt: ATMOSPHERE_RADIUS_KM,
    g: MIE_G,
    sunDir: sunDirection(p.sunElevationDeg, p.sunAzimuthDeg),
    mieScale,
    skyAmbient: [0.02, 0.03, 0.05],
  }
}

/** 默认可调参数 */
export function defaultParams(): AtmosphereParams {
  return {
    sunElevationDeg: 50,
    sunAzimuthDeg: 0,
    turbidity: 1.0,
    aerosolDensity: 0.2,
    ozoneAbsorption: 0.0,
    altitudeKm: 0.002,
  }
}
